import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type { KeySearchHit } from '@greco-gls/shared';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { searchKeys } from '@/api/endpoints';
import { queryKeys } from '@/api/queryClient';
import { useDebouncedValue, useHotkeys } from '@/hooks';
import { Highlight } from '@/modules/search/Highlight';
import {
  keySearchHitPath,
  keySearchPagePath,
  MIN_SEARCH_LENGTH,
  SEARCH_DEBOUNCE_MS,
} from '@/modules/search/link';

/** Hits in the drop-down; the rest is one click away on the search page. */
const PREVIEW_SIZE = 8;

/** The last row of the drop-down: the same query on the search page. */
interface AllResults {
  kind: 'all';
  query: string;
  total: number | null;
}

type Option = KeySearchHit | AllResults;

const isAllResults = (option: Option): option is AllResults => 'kind' in option;

/**
 * The key search in the app bar (spec §7.5A): every project the user can read and every package,
 * from any screen, focused with Ctrl+K.
 *
 * A request goes out once typing pauses for `SEARCH_DEBOUNCE_MS`, never per keystroke. The first
 * hit is highlighted, so Enter opens it; the last row opens the full result list.
 */
export function GlobalKeySearch() {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [input, setInput] = useState('');
  const typed = input.trim();
  const term = useDebouncedValue(typed, SEARCH_DEBOUNCE_MS);
  const searchable = term.length >= MIN_SEARCH_LENGTH && term === typed;

  const preview = useQuery({
    queryKey: queryKeys.keySearchFor({ q: term, limit: PREVIEW_SIZE }),
    queryFn: () => searchKeys({ q: term, limit: PREVIEW_SIZE }),
    enabled: searchable,
  });

  useHotkeys([
    {
      key: 'k',
      ctrl: true,
      handler: () => {
        inputRef.current?.focus();
        inputRef.current?.select();
      },
    },
  ]);

  // Nothing from an earlier query while the next one is pending: Enter would open a stale hit.
  const hits = searchable && preview.data ? preview.data.items : [];
  const options: Option[] = hits.length
    ? [...hits, { kind: 'all', query: term, total: preview.data?.total ?? null }]
    : [];
  const waiting = typed.length >= MIN_SEARCH_LENGTH && (!searchable || preview.isFetching);

  const open = (path: string) => {
    setInput('');
    inputRef.current?.blur();
    void navigate(path);
  };

  return (
    <Autocomplete<Option>
      size="small"
      options={options}
      // The API ranked and filtered them already.
      filterOptions={(all) => all}
      autoHighlight
      clearOnEscape
      value={null}
      inputValue={input}
      onInputChange={(_event, value, reason) => {
        // `reset` is the Autocomplete writing the chosen label back; the field empties on navigate.
        if (reason !== 'reset') setInput(value);
      }}
      onChange={(_event, option) => {
        if (!option) return;
        open(isAllResults(option) ? keySearchPagePath(option.query) : keySearchHitPath(option));
      }}
      getOptionLabel={(option) => (isAllResults(option) ? option.query : option.name)}
      getOptionKey={(option) => (isAllResults(option) ? 'all-results' : option.keyId)}
      loading={waiting}
      loadingText="Searching…"
      noOptionsText={
        typed.length < MIN_SEARCH_LENGTH ? 'Type at least two characters' : 'No key matches'
      }
      data-help="shell-search"
      // As tall as the other top-bar controls (38 px), on the page's ground colour so it reads as
      // a place to type rather than another button.
      sx={{
        width: { xs: 180, md: 300, lg: 340 },
        '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
          py: '3.5px',
          bgcolor: 'background.default',
        },
      }}
      slotProps={{
        popper: { placement: 'bottom-end', style: { width: 560 } },
        listbox: { sx: { maxHeight: 520 } },
      }}
      renderOption={(props, option) => {
        const { key, ...rest } = props;
        if (isAllResults(option)) {
          return (
            <Box component="li" key={key} {...rest} sx={{ borderTop: 1, borderColor: 'divider' }}>
              <Typography sx={{ fontSize: 13, color: 'primary.main' }}>
                {option.total === null ? 'Show all results' : `Show all ${option.total} results`}{' '}
                for “{option.query}”
              </Typography>
            </Box>
          );
        }
        const text = option.matchedValue ?? option.sourceValue;
        return (
          <Box component="li" key={key} {...rest}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  sx={{ fontFamily: 'monospace', fontSize: 13, flex: 1, minWidth: 0 }}
                  noWrap
                  title={option.name}
                >
                  <Highlight text={option.name} query={term} />
                </Typography>
                <Chip
                  label={
                    option.linkedVia
                      ? `${option.ownerCode} → ${option.linkedVia}`
                      : option.ownerCode
                  }
                  variant={option.ownerType === 'package' ? 'outlined' : 'filled'}
                  sx={{ height: 20, fontSize: 11 }}
                />
              </Box>
              {text ? (
                <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap title={text}>
                  {option.matchedLocale && option.matchedLocale !== option.sourceLocale
                    ? `${option.matchedLocale}: `
                    : ''}
                  <Highlight text={text} query={term} />
                </Typography>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder="Search keys (Ctrl+K)"
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { ...params.slotProps.htmlInput, 'aria-label': 'Search keys' },
          }}
        />
      )}
    />
  );
}
